/**
 * HoDocumentsTab - Documents tab for active project detail.
 * Matched exactly to extracted_assets_2: bf832ac1-9d48-45db-a651-9a4805854e40
 */

import * as React from "react";
import { toast } from "sonner";
import { SkeletonRows } from "@/components/ui/skeleton";
import {
	type ProjectDocumentListItem,
	useProjectDocuments,
} from "../../../hooks/use-projects";
import {
	createProjectDocument,
	downloadProjectDocument,
} from "../../../services/project-documents-api.service";
import { DocumentUploadDialog } from "../../documents/document-upload-dialog";
import { Btn, FloatPanel, Icn } from "../design-primitives";

type DocumentType = "Legal" | "Financial" | "Design" | "Drawings" | "Progress";

interface HoDocument {
	id: string;
	projectId: string;
	type: string;
	title: string;
	description: string | null;
	createdByUserId: string;
	createdByName: string | null;
	createdAt: string;
	updatedAt: string;
	latestVersion: {
		id: string;
		documentId: string;
		versionNumber: number;
		originalFileName: string;
		mimeType: string;
		fileSize: number;
		uploadedByUserId: string;
		uploadedByName: string | null;
		createdAt: string;
		downloadUrl: string;
		isCurrent: boolean;
	};
	uiType: DocumentType;
	uiSize: string;
	uiBy: string;
	uiDate: string;
}

const API_TYPE_TO_UI: Record<string, DocumentType> = {
	contract: "Legal",
	invoice: "Financial",
	permit: "Drawings",
};

function formatFileSize(bytes: number): string {
	if (bytes === 0) return "—";
	const k = 1024;
	const sizes = ["Bytes", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

function formatDate(dateStr: string): string {
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
}

function transformDocument(doc: ProjectDocumentListItem): HoDocument {
	const version = doc.latestVersion;
	return {
		...doc,
		uiType: API_TYPE_TO_UI[doc.type] || "Design",
		uiSize: formatFileSize(version.fileSize),
		uiBy: version.uploadedByName || "Unknown",
		uiDate: formatDate(version.createdAt),
	};
}

interface HoDocumentsTabProps {
	projectId: string;
}

export function HoDocumentsTab({ projectId }: HoDocumentsTabProps) {
	const [downloaded, setDownloaded] = React.useState<Set<string>>(new Set());
	const [downloading, setDownloading] = React.useState<Set<string>>(new Set());
	const [isUploadDialogOpen, setIsUploadDialogOpen] = React.useState(false);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const {
		data: documentsData,
		isLoading,
		error,
		refetch,
	} = useProjectDocuments(projectId);

	const documents: HoDocument[] = React.useMemo(
		() =>
			documentsData?.documents
				? documentsData.documents.map(transformDocument)
				: [],
		[documentsData],
	);

	const readyCount = documents.length;
	const missingCount = 0;

	const handleDownload = async (doc: HoDocument) => {
		try {
			setDownloading((prev) => new Set(prev).add(doc.id));
			await downloadProjectDocument(
				doc.latestVersion.downloadUrl,
				doc.latestVersion.originalFileName,
			);
			setDownloaded((prev) => new Set(prev).add(doc.id));
		} catch (err) {
			console.error("Failed to download document:", err);
		} finally {
			setDownloading((prev) => {
				const next = new Set(prev);
				next.delete(doc.id);
				return next;
			});
		}
	};

	const handleCreateDocument = async (data: {
		title: string;
		type: "contract" | "invoice" | "permit";
		description?: string;
		file: File;
	}) => {
		setIsSubmitting(true);
		try {
			await createProjectDocument(projectId, data);
			toast.success("Project document uploaded");
			await refetch();
		} catch {
			toast.error("Failed to upload project document");
			throw new Error("upload failed");
		} finally {
			setIsSubmitting(false);
		}
	};

	if (isLoading) {
		return (
			<SkeletonRows
				label="Loading documents..."
				className="p-10"
				rowClassName="h-16 rounded-xl"
			/>
		);
	}

	if (error) {
		return (
			<div
				style={{
					padding: 40,
					textAlign: "center",
					color: "var(--color-dashboard-pill-risk)",
				}}
			>
				Failed to load documents
			</div>
		);
	}

	if (documents.length === 0) {
		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 20,
					padding: "40px 20px",
					textAlign: "center",
				}}
			>
				<div
					style={{
						width: 52,
						height: 52,
						borderRadius: 10,
						background: "var(--color-dashboard-divider)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Icn
						name="fileText"
						size={22}
						color="var(--color-dashboard-accent-dark)"
					/>
				</div>
				<div>
					<p
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 14,
							fontWeight: 600,
							color: "var(--color-dashboard-text)",
							margin: "0 0 4px",
						}}
					>
						No documents yet
					</p>
					<p
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 12.5,
							color: "var(--color-dashboard-muted)",
							margin: 0,
						}}
					>
						Upload contracts, invoices, or permits to keep everything in one
						place.
					</p>
				</div>
				<Btn
					variant="primary"
					size="sm"
					icon={
						<Icn
							name="upload"
							size={13}
							color="var(--color-dashboard-on-fill)"
						/>
					}
					onClick={() => setIsUploadDialogOpen(true)}
				>
					Upload document
				</Btn>
			</div>
		);
	}

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
			<div
				style={{
					display: "flex",
					justifyContent: "flex-end",
				}}
			>
				<Btn
					variant="primary"
					size="sm"
					icon={
						<Icn
							name="upload"
							size={13}
							color="var(--color-dashboard-on-fill)"
						/>
					}
					onClick={() => setIsUploadDialogOpen(true)}
				>
					Upload document
				</Btn>
			</div>

			<div
				style={{
					display: "flex",
					gap: 12,
					flexWrap: "wrap",
				}}
			>
				<div
					style={{
						padding: "10px 16px",
						background: "var(--color-dashboard-pill-done-bg)",
						border: "1px solid var(--color-dashboard-success-border)",
						borderRadius: 7,
						display: "flex",
						alignItems: "center",
						gap: 8,
					}}
				>
					<Icn
						name="checkCircle"
						size={14}
						color="var(--color-dashboard-success)"
					/>
					<span
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 12,
							color: "var(--color-dashboard-success)",
							fontWeight: 500,
						}}
					>
						{readyCount} available
					</span>
				</div>
				{missingCount > 0 && (
					<div
						style={{
							padding: "10px 16px",
							background: "var(--color-dashboard-pill-risk-bg)",
							border: "1px solid var(--color-dashboard-danger-border)",
							borderRadius: 7,
							display: "flex",
							alignItems: "center",
							gap: 8,
						}}
					>
						<Icn
							name="info"
							size={14}
							color="var(--color-dashboard-pill-risk)"
						/>
						<span
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 12,
								color: "var(--color-dashboard-pill-risk)",
								fontWeight: 500,
							}}
						>
							{missingCount} missing
						</span>
					</div>
				)}
			</div>

			{documents.map((d) => {
				const isDownloaded = downloaded.has(d.id);
				const isDownloading = downloading.has(d.id);
				return (
					<FloatPanel key={d.id} elev={1} padding={0}>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 16,
								padding: "16px 22px",
							}}
						>
							<div
								style={{
									width: 40,
									height: 40,
									borderRadius: 7,
									background: "var(--color-dashboard-divider)",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									color: "var(--color-dashboard-accent-dark)",
									flexShrink: 0,
								}}
							>
								<Icn name="fileText" size={18} />
							</div>

							<div style={{ flex: 1, minWidth: 0 }}>
								<div
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 14,
										fontWeight: 600,
										color: "var(--color-dashboard-text)",
									}}
								>
									{d.title}
								</div>
								<div
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 8.5,
										letterSpacing: "0.08em",
										color: "var(--color-dashboard-muted)",
										marginTop: 3,
										textTransform: "uppercase",
									}}
								>
									{d.uiType} · {d.uiSize} · {d.uiBy} · {d.uiDate}
								</div>
							</div>

							{isDownloaded ? (
								<button
									type="button"
									onClick={() => {
										const next = new Set(downloaded);
										next.delete(d.id);
										setDownloaded(next);
									}}
									style={{
										display: "flex",
										alignItems: "center",
										gap: 6,
										padding: "0 14px",
										height: 32,
										borderRadius: 6,
										border: "none",
										background: "var(--color-dashboard-pill-done-bg)",
										cursor: "pointer",
										fontFamily: "var(--font-body)",
										fontSize: 12,
										fontWeight: 500,
										color: "var(--color-dashboard-success)",
									}}
								>
									<Icn
										name="check"
										size={13}
										color="var(--color-dashboard-success)"
									/>
									Saved
								</button>
							) : (
								<button
									type="button"
									onClick={() => handleDownload(d)}
									disabled={isDownloading}
									style={{
										display: "flex",
										alignItems: "center",
										gap: 6,
										padding: "0 14px",
										height: 32,
										borderRadius: 6,
										border: "1px solid var(--color-border-default)",
										background: "var(--color-dashboard-paper)",
										cursor: isDownloading ? "wait" : "pointer",
										fontFamily: "var(--font-body)",
										fontSize: 12,
										fontWeight: 500,
										color: "var(--color-dashboard-muted-dark)",
										opacity: isDownloading ? 0.7 : 1,
									}}
								>
									<Icn
										name={isDownloading ? "refreshCw" : "download"}
										size={13}
										color="var(--color-dashboard-muted-dark)"
									/>
									{isDownloading ? "Downloading..." : "Download"}
								</button>
							)}
						</div>
					</FloatPanel>
				);
			})}

			<DocumentUploadDialog
				open={isUploadDialogOpen}
				onOpenChange={setIsUploadDialogOpen}
				onSubmit={handleCreateDocument}
				isSubmitting={isSubmitting}
			/>
		</div>
	);
}
